// src/screens/Marketplace/SavedProductsScreen.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import './SavedProductsScreen.css';

import placeholderLogo from '../../assets/placeholder-logo.png';
import backArrow from '../../assets/backarrow-icon.png';

import { useSavedProducts } from '../../context/SavedProductsContext'; // ✅ global context

function SavedProductsScreen({ user }) {
  const navigate = useNavigate();
  const { savedProductData, fetchSavedProducts } = useSavedProducts(); // ✅ full product objects
  const [piToUsdRate, setPiToUsdRate] = useState(null);

  useEffect(() => {
    if (user?.username) {
      fetchSavedProducts(user.username);
    }
  }, [user.username, fetchSavedProducts]);

  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await api.get(`/convert/rate`);
        if (res.data?.piToUsd) {
          setPiToUsdRate(res.data.piToUsd);
        }
      } catch (err) {
        console.error('❌ Failed to fetch Pi rate:', err);
      }
    };
    fetchRate();
  }, []);

  // ---------- pricing helpers ----------
  const fmtPi = (n) =>
    typeof n === 'number' && Number.isFinite(n) ? `${n.toFixed(2)} Pi` : null;

  const getPriceParts = (product) => {
    const pv = product?.pricingView;
    if (pv && typeof pv.basePrice === 'number') {
      const base = pv.basePrice;
      const eff = pv.effectivePrice ?? pv.basePrice;
      const onSale = !!pv.isOnFlashSale && eff !== null && eff < base;
      const percent = pv.percentOff ?? null;
      return { base, eff, onSale, percent };
    }

    // raw (fixed Pi fallback)
    if (product?.pricingType === 'raw' && typeof product?.price === 'number') {
      return { base: product.price, eff: product.price, onSale: false, percent: null };
    }

    // dynamic fallback
    if (product?.pricingType === 'dynamic' && typeof product?.usdPrice === 'number' && piToUsdRate) {
      const base = +(product.usdPrice / piToUsdRate).toFixed(2);
      return { base, eff: base, onSale: false, percent: null };
    }

    return { base: null, eff: null, onSale: false, percent: null };
  };
  // -------------------------------------

  return (
    <div className="saved-products-container">
      <div className="saved-top-bar">
        <img
          src={backArrow}
          alt="Back"
          className="saved-back-icon"
          onClick={() => navigate(-1)}
        />
        <h2 className="saved-title">Saved Products</h2>
      </div>

      <div className="saved-scroll-content">
        {savedProductData.length === 0 ? (
          <p className="empty-msg">You haven't saved any products yet.</p>
        ) : (
          <div className="saved-products-grid">
            {savedProductData.map((product) => {
              const { base, eff, onSale, percent } = getPriceParts(product);

              return (
                <div
                  key={product._id}
                  className="saved-product-card"
                  onClick={() => navigate(`/product/${product._id}`)}
                >
                  {/* SALE BADGE (top-left) */}
                  {onSale && (
                    <div className="saved-sale-badge">
                      -{percent ?? (base && eff ? Math.round(((base - eff) / base) * 100) : 0)}%
                    </div>
                  )}

                  {/* Image */}
                  <img
                    src={product.media?.[0] || placeholderLogo}
                    alt="product"
                    className="saved-product-img"
                  />

                  {/* Info */}
                  <div className="saved-product-info">
                    <p className="product-title">{product.name}</p>

                    {/* Single price row */}
                    <div className="saved-price-row">
                      <span className={onSale ? 'saved-price saved-price-sale' : 'saved-price'}>
                        {fmtPi(onSale ? eff : (eff ?? base))}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default SavedProductsScreen;
